import { HttpEvent, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { Option } from '@sapphire/result';

import { AuthActionsFacade, AuthSelectorsFacade } from '@ps/auth-data-access';

import { RequestUser } from './domain/request-user';
import { RequestClassifier } from './domain/request-classifier';
import { RouteContext } from './domain/route-context';

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const router = inject(Router);
  const _authSelectorsFacade = inject(AuthSelectorsFacade);
  const _authActionsFacade = inject(AuthActionsFacade);

  const request = new RequestClassifier(req);
  if (request.isForPublicUrl()) {
    return next(req);
  }

  return _authSelectorsFacade
    .user()
    .filter((user) => !!user.token)
    .match({
      some: (user) => {
        const routeContext = new RouteContext(req.url, pageUrl(router));
        const requestUser = RequestUser.from(routeContext, {
          user,
          hasServiceOperatorAccess:
            _authSelectorsFacade.hasServiceOperatorAccess(),
          impersonatedBuyer: _authSelectorsFacade.impersonatedBuyer(),
        });

        const clonedReq = req.clone({
          setHeaders: {
            'service-user-id': requestUser.id(),
            'access-token': requestUser.token(),
          },
        });
        return next(clonedReq);
      },
      none: () => {
        _authActionsFacade.logout();
        return next(req);
      },
    });
}

// `router.url` lags behind an in-flight navigation, so resolver requests use its target URL.
function pageUrl(router: Router): string {
  return Option.from(router.currentNavigation())
    .map((navigation) => navigation.finalUrl ?? navigation.extractedUrl)
    .mapOr(router.url, (url) => url.toString());
}
